import {
  Banner,
  Button,
  DATE_FORMAT,
  Table,
  Tag,
  Tooltip,
} from '@dev-spendesk/grapes';
import { add, format, fromNumber, greaterThan } from 'ezmoney';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { PageNoResult } from 'common/components/PageNoResult/PageNoResultContainer';
import { QuerySuspenseBase } from 'common/components/QuerySuspenseBase/QuerySuspenseBase';
import { SupplierLogo } from 'common/components/SupplierLogo';
import { UnexpectedError } from 'common/components/UnexpectedError/UnexpectedError';
import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import {
  useRemoveRequestsFromRequestsDraftCache,
  useRequestsDraftQuery,
} from 'modules/requests/api/useRequestsDraftQuery';
import { useBulkSubmitDraftsMutation } from 'modules/requests/hooks/useBulkSubmitDraftsMutation';
import { SubmitDraftsModal } from 'modules/requests/list/components/RequestsListBox/SubmitDraftsModal';
import { recordRequestTypetoI18NKey } from 'modules/requests/models/requestType';
import { fetchRequestsStatsSuccess } from 'modules/requests/redux/legacy/actions';
import { getRequestsStats } from 'modules/requests/redux/legacy/selectors';
import { type SubnavigationItem } from 'modules/requests/utils/navigation';
import { type GetV2ApiCompanyIdRequestsDraftResponseOK } from 'src/client/spendesk-api-types';
import { FeedbackModal } from 'src/core/common/components/FeedbackModal';
import { TypeToHighlightIcon } from 'src/core/common/components/TypeToHighlightIcon/TypeToHighlightIcon';
import { useParams } from 'src/core/common/hooks/useParams';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { UserAvatar } from 'src/core/modules/users/components';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { draftSections, draftsectionsByRequestType } from './utils';
import { RequestsListLoader } from '../RequestsListsLoader';
import { DraftRequestsTableActions } from './DraftRequestsTableActions';

type DraftRequestsTableProps = {
  type: SubnavigationItem;
};

export const DraftRequestsTable = (props: DraftRequestsTableProps) => {
  const requestsDraftQueryState = useRequestsDraftQuery();
  return (
    <QuerySuspenseBase
      queryState={requestsDraftQueryState}
      fallback={() => <UnexpectedError />}
      loading={<RequestsListLoader />}
    >
      {(requestsDraftQuery) => (
        <DraftRequestsTableContent
          requestsDraft={requestsDraftQuery}
          type={props.type}
        />
      )}
    </QuerySuspenseBase>
  );
};

const DraftRequestsTableContent = ({
  requestsDraft,
  type,
}: {
  requestsDraft: GetV2ApiCompanyIdRequestsDraftResponseOK;
} & DraftRequestsTableProps) => {
  const { localeFormat, t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const dispatch = useDispatch();
  const stats = useSelector(getRequestsStats);
  const { id: pathRequestId } = useParams(routes.REQUESTS.path);
  const company = useCompany();
  const user = useUser();

  const history = useHistory();

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedRowIds, setSelectedRowsIds] = useState<string[]>([]);
  const [submitModalState, setSubmitModalState] = useState<
    | {
        totalAmount: string;
        numberOfRequests: number;
        submittedIds: string[];
      }
    | undefined
  >(undefined);

  const bulkSubmitDrafts = useBulkSubmitDraftsMutation();
  const removeRequestsFromRequestsDraftCache =
    useRemoveRequestsFromRequestsDraftCache();

  const { requests, suppliers, users } = requestsDraft.data;

  useEffect(() => {
    let expenseClaimsCount = 0;
    let invoiceAndCreditNoteCount = 0;
    for (const request of requests) {
      if (request.type === 'invoice' || request.type === 'credit_note') {
        invoiceAndCreditNoteCount++;
      } else if (request.type === 'expense') {
        expenseClaimsCount++;
      }
    }
    track(AnalyticEventName.DRAFT_PAGE_COUNT, {
      requestCount: requests.length,
      invoiceAndCreditNoteCount,
      expenseClaimsCount,
    });
  }, [requests.length]);

  if (requests.length === 0) {
    return (
      <div className="mt-40 h-full">
        <PageNoResult mode="draftRequest" hasFiltersApplied={false} />
      </div>
    );
  }

  const showAvatarColumn = requests.some(
    (request) => request.userId !== user.id,
  );

  return (
    <ErrorBoundary
      context={{
        team: 'travel-and-expenses',
        scope: 'requests::list::drafts',
      }}
    >
      {requests.some((request) => request.type === 'expense') && (
        <>
          <Banner
            className="mt-24"
            variant="neutral"
            title={t('requests.draftsList.bannerTitle')}
            actions={
              <Button
                variant="secondaryNeutral"
                text={t('betaFeature.giveFeedback')}
                onClick={() => {
                  setIsFeedbackModalOpen(true);
                }}
              />
            }
          >
            <Trans
              i18nKey="requests.draftsList.bannerContent"
              components={{
                a: (
                  <a
                    href="https://helpcenter.spendesk.com/articles/10821826"
                    target="_blank"
                    rel="noreferrer"
                  >
                    -
                  </a>
                ),
              }}
            />
          </Banner>
          <FeedbackModal
            onCancel={() => setIsFeedbackModalOpen(false)}
            onConfirm={() => setIsFeedbackModalOpen(false)}
            isOpen={isFeedbackModalOpen}
            title={t('betaFeature.feedbackModal.title')}
            subtitle={t('requests.draftsList.feedbackModalSubtitle')}
            confirmText={t(
              'betaFeature.feedbackModal.submitAndSwitchToOldVersion',
            )}
            cancelText={t('betaFeature.feedbackModal.cancel')}
            feedbackLabel={t('betaFeature.feedbackModal.textLabel')}
            feedbackTags={['drafts']}
          />
        </>
      )}
      <Table
        className="my-24"
        columns={[
          ...(showAvatarColumn
            ? [
                {
                  id: 'user',
                  width: '272px',
                  renderCell: (
                    request: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'][0],
                  ) => {
                    const requestUser = users.find(
                      (_user) => _user.id === request.userId,
                    );
                    if (!requestUser) {
                      return (
                        <div className="flex items-center gap-8">
                          <span className="text-content-secondary-bg-secondary">
                            {t('misc.na')}
                          </span>
                        </div>
                      );
                    }
                    const fullName = `${requestUser.firstName} ${requestUser.lastName}`;
                    return (
                      <div className="max-w-full flex items-center gap-8">
                        <UserAvatar
                          id={request.userId ?? ''}
                          name={fullName}
                          avatar={requestUser.avatarUrl ?? ''}
                        />
                        <div className="flex flex-col truncate body-m">
                          <span className="text-content-primary">
                            <EllipsisTooltip text={fullName} data-mask />
                          </span>
                        </div>
                      </div>
                    );
                  },
                  header: t('requests.usersDelegation.behalfLabel'),
                },
              ]
            : []),
          {
            id: 'type',
            width: '56px',
            align: 'center',
            renderCell: (request) => (
              <Tooltip
                content={
                  t(recordRequestTypetoI18NKey[request.type]) ?? request.type
                }
              >
                <TypeToHighlightIcon type={request.type} />
              </Tooltip>
            ),
            header: t('requests.toApproveList.type'),
          },
          {
            id: 'supplier',
            renderCell: (request) => {
              if (
                request.type === 'per_diem_allowance' ||
                request.type === 'mileage_allowance'
              ) {
                return t('misc.na');
              }
              const supplier = suppliers.find(
                ({ id }) => id === request.supplierId,
              );
              if (!request.supplierId || !supplier) {
                const extractedSupplierName = getExtractedSupplierName(request);
                if (extractedSupplierName) {
                  return (
                    <div className="flex gap-8">
                      <div className="flex items-center gap-8 truncate">
                        <SupplierLogo name={extractedSupplierName} size={32} />
                        <EllipsisTooltip text={extractedSupplierName} />
                      </div>
                    </div>
                  );
                }

                return '';
              }

              return (
                <div className="flex gap-8">
                  <div className="flex items-center gap-8 truncate">
                    <SupplierLogo name={supplier.name} size={32} />
                    <EllipsisTooltip text={supplier.name} />
                  </div>
                </div>
              );
            },
            header: t('requests.toApproveList.supplier'),
          },
          {
            id: 'description',
            renderCell: (request) => (
              <div className="truncate">
                <EllipsisTooltip text={request.description} />
              </div>
            ),
            header: t('requests.toApproveList.description'),
          },
          {
            id: 'status',
            width: '272px',
            renderCell: (request) => {
              if (request.draftCompletion?.status === 'complete') {
                return (
                  <div className="text-wrap">
                    <Tag variant="info" iconName="circle-check">
                      {t('requests.draftsList.readyToSubmit')}
                    </Tag>
                  </div>
                );
              }

              return (
                <div className="text-wrap">
                  <Tag variant="warning" iconName="triangle-warning">
                    {t('requests.draftsList.missingInformation')}
                  </Tag>
                </div>
              );
            },
            header: t('misc.status'),
          },
          {
            id: 'date',
            width: '144px',
            renderCell: (request) =>
              localeFormat(new Date(request.createdAt), DATE_FORMAT.MEDIUM),
            header: t('requests.toApproveList.date'),
          },
          {
            id: 'amount',
            width: '144px',
            renderCell: (request) => {
              if (
                request.amounts.declared.inOriginalCurrency &&
                request.amounts.declared.inCompanyCurrency &&
                request.amounts.declared.inCompanyCurrency.currency !==
                  request.amounts.declared.inOriginalCurrency.currency
              ) {
                return (
                  <div className="flex flex-col">
                    <span>
                      {format(request.amounts.declared.inCompanyCurrency)}
                    </span>
                    <span className="text-content-secondary-bg-secondary">
                      {format(request.amounts.declared.inOriginalCurrency)}
                    </span>
                  </div>
                );
              }
              if (request.amounts.declared.inCompanyCurrency) {
                return format(request.amounts.declared.inCompanyCurrency);
              }
              const extractedAmount = getExtractedAmount(request);
              if (extractedAmount) {
                return format(extractedAmount);
              }
              return '';
            },
            header: t('requests.toApproveList.amount'),
          },
        ]}
        data={requests}
        groupBy={(request) => draftsectionsByRequestType[request.type]}
        getRowId={(request) => request.id}
        getIsRowActive={(request) => pathRequestId === request.id}
        selectedRowIds={selectedRowIds}
        onRowClick={(request) => {
          history.push(
            routeFor(routes.REQUESTS.path, {
              company: company.id,
              id: request.id,
              type,
            }),
          );
        }}
        onRowSelectionChange={(_, id, checked) => {
          setSelectedRowsIds((options) => {
            if (checked) {
              return options.concat(id);
            }
            return options.filter((optionId) => optionId !== id);
          });
        }}
        onAllRowsSelectionChange={(_, ids, checked) => {
          setSelectedRowsIds(checked ? ids : []);
        }}
        renderGroupedRowHeader={(value, requestsForGroup) => {
          const section = draftSections[value];
          if (!section) {
            return null;
          }
          const translationKey = section.translationKey;

          const submittableRequests = requestsForGroup.filter(
            (request) => request.draftCompletion?.status === 'complete',
          );

          const sum = submittableRequests.reduce(
            (accumulator, row) => {
              if (row.amounts.declared?.inCompanyCurrency) {
                return add(
                  accumulator,
                  row.amounts.declared?.inCompanyCurrency,
                );
              }

              return accumulator;
            },
            fromNumber(0, company.currency as string),
          );

          const totalSum = requestsForGroup.reduce(
            (accumulator, row) => {
              if (row.amounts.declared?.inCompanyCurrency) {
                return add(
                  accumulator,
                  row.amounts.declared?.inCompanyCurrency,
                );
              }

              return accumulator;
            },
            fromNumber(0, company.currency as string),
          );

          return (
            <div className="flex items-center py-8 uppercase text-content-secondary-bg-secondary body-s">
              <span className="">{t(translationKey)}</span>
              <div className="ml-auto flex items-center gap-8">
                {submittableRequests.length > 0 &&
                greaterThan(sum, fromNumber(0, company.currency)) ? (
                  <>
                    <span>
                      {t('requests.draftsList.readyToSubmitSubHeader', {
                        ratio: `${submittableRequests.length}/${requestsForGroup.length}`,
                      })}
                    </span>
                    <Button
                      variant="secondaryNeutral"
                      text={t('requests.draftsList.submitAll', {
                        amount: format(sum),
                        count: submittableRequests.length,
                      })}
                      onClick={() =>
                        setSubmitModalState({
                          totalAmount: format(sum),
                          numberOfRequests: submittableRequests.length,
                          submittedIds: submittableRequests.map(
                            (request) => request.id,
                          ),
                        })
                      }
                    />
                  </>
                ) : (
                  <>
                    <span>
                      {t('misc.requestWithCount', {
                        count: requestsForGroup.length,
                      })}
                    </span>
                    <span
                      className="h-4 w-4 rounded-[50%]"
                      style={{ background: 'var(--grapes-color-border-border-default)' }}
                    />
                    <span>{format(totalSum)}</span>
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
      <DraftRequestsTableActions
        selectedRowIds={selectedRowIds}
        unselectRequests={() => setSelectedRowsIds([])}
        setSelectedRowIds={setSelectedRowsIds}
        requests={requests}
        setSubmitModalState={setSubmitModalState}
      />
      {submitModalState ? (
        <SubmitDraftsModal
          isOpen
          onCancel={() => setSubmitModalState(undefined)}
          onProcessed={async () => {
            const {
              data: { successIds, errorIds },
            } = await bulkSubmitDrafts.mutateAsync(
              submitModalState.submittedIds,
            );
            if (errorIds.length > 0) {
              dangerNotif(
                t('requests.panel.bulkExpenseSubmit.errorNotif', {
                  count: errorIds.length,
                }),
              );
            }
            if (successIds.length > 0) {
              successNotif(
                t('requests.panel.bulkExpenseSubmit.successNotif', {
                  count: successIds.length,
                }),
              );
            }
            track(AnalyticEventName.DRAFT_SUBMIT_DRAFTS, {
              type: 'Expense Claim',
              requestCount: submitModalState.submittedIds.length,
            });
            removeRequestsFromRequestsDraftCache(successIds);
            setSubmitModalState(undefined);
            dispatch(
              fetchRequestsStatsSuccess({
                ...stats,
                drafts: stats.drafts - successIds.length,
              }),
            );
          }}
          totalAmount={submitModalState?.totalAmount ?? ''}
          numberOfRequests={submitModalState?.numberOfRequests ?? 0}
        />
      ) : null}
    </ErrorBoundary>
  );
};

const getExtractedSupplierName = (
  request: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'][0],
) => {
  const receipts = request.receipts ?? [];
  for (const receipt of receipts) {
    const extractedInformations = receipt.extractedInformations ?? [];
    for (const extractedInformation of extractedInformations) {
      if (extractedInformation.supplier?.name) {
        return extractedInformation.supplier.name;
      }
    }
  }
  return '';
};

const getExtractedAmount = (
  request: GetV2ApiCompanyIdRequestsDraftResponseOK['data']['requests'][0],
) => {
  const receipts = request.receipts ?? [];
  for (const receipt of receipts) {
    const extractedInformations = receipt.extractedInformations ?? [];
    for (const extractedInformation of extractedInformations) {
      if (extractedInformation.amount) {
        return extractedInformation.amount;
      }
    }
  }
  return '';
};
